import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SecureAuth } from "asset/icons";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import { COMPLETE_LOGIN } from "store/action";
import { checkAutoLogin } from "store/authServices";

const Confirm2FA = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        const pendingLogin = sessionStorage.getItem("pendingLogin");

        if (!pendingLogin) {
            navigate("/auth/signin", { replace: true });
        }
    }, [navigate]);

    const handleChange = (index, value) => {
        if (/^[0-9]?$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);
            setError("");

            if (value && index < code.length - 1) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerify = async () => {
        const joinedCode = code.join("");
        if (joinedCode.length !== 6) {
            setError("Please enter the 6-digit code from your authenticator app.");
            return;
        }

        const pendingLoginString = sessionStorage.getItem("pendingLogin");
        if (!pendingLoginString) {
            setError("Your sign-in session expired. Please sign in again.");
            return navigate("/auth/signin", { replace: true });
        }

        const pendingLogin = JSON.parse(pendingLoginString);

        try {
            setLoading(true);
            setError("");

            const verifyResponse = await axios.post(
                "/v1/users/mfa-verify",
                { code: joinedCode },
                {
                    headers: {
                        Authorization: `Bearer ${pendingLogin.token}`,
                    },
                }
            );

            const verifiedLoginData = verifyResponse?.data?.data || {};

            if (!verifiedLoginData?.token) {
                throw new Error("Missing permanent token from MFA verification response.");
            }

            COMPLETE_LOGIN(verifiedLoginData, dispatch);
            sessionStorage.removeItem("pendingLogin");
            await checkAutoLogin(dispatch);

            if (verifiedLoginData?.user?.pinStatus === "NOT_VERIFIED") {
                return navigate("/auth/setup-pin", { replace: true });
            }

            const lastVisitedPath = sessionStorage.getItem("lastVisitedPath");
            if (
                lastVisitedPath &&
                lastVisitedPath !== "/" &&
                lastVisitedPath !== "/auth/signin"
            ) {
                sessionStorage.removeItem("lastVisitedPath");
                return navigate(lastVisitedPath, { replace: true });
            }

            navigate("/dashboard", { replace: true });
        } catch (verifyError) {
            const errorMessage =
                verifyError?.response?.data?.message ||
                verifyError?.message ||
                "Invalid authentication code. Please try again.";

            if (errorMessage === "Your session has expired. Please log in again.") {
                sessionStorage.removeItem("pendingLogin");
                setError("");
                navigate("/auth/signin", { replace: true });
                return;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl w-full max-w-[650px] mx-auto border border-[#EBEDF9]">
            <div className="!pt-5 pl-2">
                <SecureAuth />
            </div>
            <div className="px-8 pb-8">
                <h2 className="heading-h2-semibold text-[#101828]">Confirm your sign-in</h2>
                <p className="mt-2  text-[#667085] aku-body-normal-regular">
                    Enter the 6-digit code from your authenticator app to securely login.
                </p>

                <div className="mt-8 flex gap-3">
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={(element) => (inputRefs.current[index] = element)}
                            type="text"
                            inputMode="numeric"
                            maxLength="1"
                            value={digit}
                            placeholder="-"
                            onChange={(event) => handleChange(index, event.target.value)}
                            onKeyDown={(event) => handleKeyDown(index, event)}
                            className="h-16 w-16  text-center rounded-xl border border-[#EAECF0] bg-[#F2F4F7] text-2xl text-[#344054] outline-none transition focus:border-[#6F00FF] focus:ring-2 focus:ring-[#E9D7FE]"
                        />
                    ))}
                </div>

                <div className="mt-4">
                    <ErrorMessage message={error} />
                </div>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <button
                        type="button"
                        onClick={() => navigate("/auth/2fa-backup")}
                        className="text-left text-sm font-medium text-[#6F00FF] hover:underline"
                    >
                        Can't access device?
                    </button>

                    <div className="flex items-center gap-6">
                        <button
                            type="button"
                            className="text-sm font-medium text-[#101828] hover:underline"
                        >
                            Having trouble?
                        </button>

                        <button
                            type="button"
                            onClick={handleVerify}
                            disabled={code.join("").length !== 6 || loading}
                            className={`rounded-lg bg-[#6F00FF] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#6f00fff0] ${
                                code.join("").length !== 6 || loading
                                    ? "cursor-not-allowed opacity-50"
                                    : ""
                            }`}
                        >
                            {loading ? <Loader /> : "Verify"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Confirm2FA;
